import React, { type ComponentType, Suspense, useMemo } from 'react';

import type { PreloadedQuery } from 'react-relay';

import type {
	ExtensionHandlers,
	ExtensionParams,
} from '@atlaskit/editor-common/extensions/extension-handler';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { ArticleSearchComponent } from '@atlassian/article-search/article-search-component';
import { HelpObjectSearchComponent } from '@atlassian/article-search/help-object-search-component';
import { RESOURCE_TILES_EXTENSION_TYPE } from '@atlassian/company-hub-common/constants';
import type { ProductDependenciesProviderProps } from '@atlassian/company-hub-common/controllers/use-product-dependencies';
import { ExtensionMode } from '@atlassian/company-hub-common/types';
import { HUB_CONNECT_EXTENSION_TYPE } from '@atlassian/hub-connect-extension/constants';
import type { HubConnectExtensionParameters } from '@atlassian/hub-connect-extension/types';
import { HUB_FORGE_EXTENSION_TYPE } from '@atlassian/hub-forge-extension/constants';
import type { HubForgeExtensionParameters } from '@atlassian/hub-forge-extension/types';
import { JSM_HELP_EXTENSION_TYPE } from '@atlassian/jsm-help-extension/constants';
import type { JSMHelpParameters } from '@atlassian/jsm-help-extension/types';
import type { ResourceTilesParameters } from '@atlassian/knowledge-cards-extension/types';
import { CALLOUT_BANNER_EXTENSION_TYPE } from '@atlassian/page-topper-extension/constants';
import type { CalloutBannerParametersType } from '@atlassian/page-topper-extension/types';
import type { rovoWidgetProviderQuery } from '@atlassian/rovo-service-agent/controllers/rovo-widget-provider/rovoWidgetProviderQuery';

export interface ProductExtensionHandlersProps {
	contentId: string;
	product: string;
	PropsProvider: ComponentType<ProductDependenciesProviderProps>;
	/**
	 * Rovo widget query preloaded at the hub level so the request runs in parallel
	 * with the callout banner's lazy chunk download. Forwarded to the callout
	 * banner extension handler.
	 */
	rovoWidgetQueryReference?: PreloadedQuery<rovoWidgetProviderQuery> | null;
}

const CalloutBannerExtensionLazy = React.lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_page-topper-extension" */ '@atlassian/page-topper-extension'
	).then((mod) => ({ default: mod.CalloutBannerExtension })),
);

const ResourceTilesExtensionLazy = React.lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_knowledge-cards-extension" */ '@atlassian/knowledge-cards-extension'
	).then((mod) => ({ default: mod.ResourceTilesExtension })),
);

const JSMHelpExtensionLazy = React.lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_jsm-help-extension" */ '@atlassian/jsm-help-extension'
	).then((mod) => ({ default: mod.JSMHelpExtension })),
);

const HubForgeExtensionLazy = React.lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_hub-forge-extension" */ '@atlassian/hub-forge-extension'
	).then((mod) => ({ default: mod.HubForgeExtension })),
);

const HubConnectExtensionLazy = React.lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_hub-connect-extension" */ '@atlassian/hub-connect-extension'
	).then((mod) => ({ default: mod.HubConnectExtension })),
);

// Host-gated: `forge-on-hubs` for Help Hub / JSM, `forge-on-csm-hubs` for CSM.
const getHubForgeAndConnectExtensionHandlers = (
	contentId: string,
	PropsProvider: ComponentType<ProductDependenciesProviderProps>,
	gateName: 'forge-on-hubs' | 'forge-on-csm-hubs' = 'forge-on-hubs',
): ExtensionHandlers => {
	if (!fg(gateName)) {
		return {};
	}
	const handlers: ExtensionHandlers = {};
	const defaultExtensionHandlerProps = {
		contentId,
		propsProvider: PropsProvider,
		mode: ExtensionMode.RENDERER,
	};
	handlers[HUB_FORGE_EXTENSION_TYPE] = (
		extension: ExtensionParams<HubForgeExtensionParameters>,
	) => (
		<Suspense fallback={<UFOLoadHold name="hub-forge-extension-loader" />}>
			<HubForgeExtensionLazy {...defaultExtensionHandlerProps} extensionNode={extension} />
		</Suspense>
	);
	handlers[HUB_CONNECT_EXTENSION_TYPE] = (
		extension: ExtensionParams<HubConnectExtensionParameters>,
	) => (
		<Suspense fallback={<UFOLoadHold name="hub-connect-extension-loader" />}>
			<HubConnectExtensionLazy {...defaultExtensionHandlerProps} extensionNode={extension} />
		</Suspense>
	);
	return handlers;
};

const getCSMExtensionHandlers = (
	contentId: string,
	PropsProvider: ComponentType<ProductDependenciesProviderProps>,
): ExtensionHandlers => {
	const handlers: ExtensionHandlers = {};

	if (!contentId) {
		return handlers;
	}

	const defaultExtensionHandlerProps = {
		contentId,
		propsProvider: PropsProvider,
		mode: ExtensionMode.RENDERER,
		CustomSearchComponent: ArticleSearchComponent,
	};

	// Callout banner Extension Handler
	handlers[CALLOUT_BANNER_EXTENSION_TYPE] = (
		extension: ExtensionParams<CalloutBannerParametersType>,
	) => (
		<Suspense fallback={<UFOLoadHold name="page-topper-extension-loader" />}>
			<CalloutBannerExtensionLazy {...defaultExtensionHandlerProps} extensionNode={extension} />
		</Suspense>
	);

	// Resource Tiles Extension Handler
	handlers[RESOURCE_TILES_EXTENSION_TYPE] = (
		extension: ExtensionParams<ResourceTilesParameters>,
	) => (
		<Suspense fallback={<UFOLoadHold name="resource-tiles-extension-loader" />}>
			<ResourceTilesExtensionLazy
				{...defaultExtensionHandlerProps}
				extensionNode={extension}
				isInEditor={false}
			/>
		</Suspense>
	);

	Object.assign(
		handlers,
		getHubForgeAndConnectExtensionHandlers(contentId, PropsProvider, 'forge-on-csm-hubs'),
	);

	return handlers;
};

const getJSMExtensionHandlers = (
	contentId: string,
	PropsProvider: ComponentType<ProductDependenciesProviderProps>,
	rovoWidgetQueryReference?: PreloadedQuery<rovoWidgetProviderQuery> | null,
): ExtensionHandlers => {
	const handlers: ExtensionHandlers = {};

	if (!contentId) {
		return handlers;
	}

	const defaultExtensionHandlerProps = {
		contentId,
		propsProvider: PropsProvider,
		mode: ExtensionMode.RENDERER,
		CustomSearchComponent: HelpObjectSearchComponent,
	};

	handlers[CALLOUT_BANNER_EXTENSION_TYPE] = (
		extension: ExtensionParams<CalloutBannerParametersType>,
	) => (
		<Suspense fallback={<UFOLoadHold name="page-topper-extension-loader" />}>
			<CalloutBannerExtensionLazy
				{...defaultExtensionHandlerProps}
				extensionNode={extension}
				rovoWidgetQueryReference={rovoWidgetQueryReference}
			/>
		</Suspense>
	);

	// JSM Help Extension Handler
	handlers[JSM_HELP_EXTENSION_TYPE] = (extension: ExtensionParams<JSMHelpParameters>) => (
		<Suspense fallback={<UFOLoadHold name="jsm-help-extension-loader" />}>
			<JSMHelpExtensionLazy {...defaultExtensionHandlerProps} extensionNode={extension} />
		</Suspense>
	);

	Object.assign(handlers, getHubForgeAndConnectExtensionHandlers(contentId, PropsProvider));

	return handlers;
};

export const useProductExtensionHandlers = (
	props: ProductExtensionHandlersProps,
): ExtensionHandlers<any> => {
	const { contentId, PropsProvider, product, rovoWidgetQueryReference } = props;

	const extensionHandlers: ExtensionHandlers = useMemo(() => {
		if (product === 'CUSTOMER_SERVICE') {
			return getCSMExtensionHandlers(contentId, PropsProvider);
		}

		// Rovo in the callout banner is only available in help hubs, so the
		// preloaded Rovo widget query is only forwarded here.
		if (product === 'HELP_HUB' && fg('enable-jsm-in-hubs')) {
			return getJSMExtensionHandlers(contentId, PropsProvider, rovoWidgetQueryReference);
		}

		return {};
	}, [product, contentId, PropsProvider, rovoWidgetQueryReference]);

	return extensionHandlers;
};
